import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * ExpandableSection provides a collapsible section with a clickable header.
 * Use as a generic progressive-disclosure wrapper for any content.
 *
 * @param {Object} props
 * @param {string} props.title - Section header text
 * @param {React.ReactNode} props.children - Content to show when expanded
 * @param {boolean} [props.defaultExpanded] - Whether to start expanded
 * @param {React.ReactNode} [props.summary] - Optional inline summary shown next to the title (always visible)
 * @param {boolean} [props.showDivider] - Whether to show a divider above the section
 * @param {Object} [props.sx] - Additional sx styles for the outer container
 */
const ExpandableSection = ({
  title,
  children,
  defaultExpanded = false,
  summary = null,
  showDivider = false,
  sx = {},
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box sx={sx}>
      {showDivider && <Divider sx={{ mb: 1 }} />}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
          borderRadius: 1,
          px: 1,
          py: 0.75,
          mx: -1,
        }}
        onClick={() => setExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
        aria-expanded={expanded}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
          {title}
        </Typography>

        {summary && (
          <Box sx={{ mr: 1 }}>
            {summary}
          </Box>
        )}

        <IconButton size="small" aria-hidden tabIndex={-1}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pt: 1, pl: 1 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

ExpandableSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  summary: PropTypes.node,
  showDivider: PropTypes.bool,
  sx: PropTypes.object,
};

export default ExpandableSection;